import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { View, StyleSheet, Text, TouchableOpacity } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { useFocusEffect } from "@react-navigation/native"
import moment from "moment"
import "moment/locale/fr"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import SummerProgramCalendar from "../summer/components/SummerProgramCalendar"
import SummerEyebrow from "../summer/components/SummerEyebrow"
import SummerHelpBanner from "../summer/components/SummerHelpBanner"
import { elide } from "@common/utils/activationSuiteDisplay"
import { useStageFunnel } from "./context/StageFunnelContext"
import { useStageTrack } from "./hooks/useStageTrack"
import { useStageFunnelGuard } from "./hooks/useStageFunnelGuard"
import { useStageFunnelConfig } from "./hooks/useStageFunnelConfig"
import { stageFunnelService } from "./services/stageFunnelService"
import {
    getTemplateName,
    sellsBySeance,
    getChapterBounds,
} from "./constants/stageContent"
import StageEditableProgram from "./components/StageEditableProgram"
import StageProgramAdjustSheet, {
    StageProgramAdjustEntry,
} from "./components/StageProgramAdjust"
import FunnelStepTicks from "@common/components/funnel/FunnelStepTicks"
import StageChapterPickerModal from "./components/StageChapterPickerModal"
import FunnelStepLayout from "@common/components/funnel/FunnelStepLayout"
import { FUNNEL_SCENES } from "@common/components/funnel/funnelScenes"
import { capitalize } from "../summer/constants/summerContent"
import { DialogM } from "@common/components/alert/dialog/Dialog"

/**
 * Étape "programme du stage" (REVEAL de valeur) — clone du reveal été : le
 * programme personnalisé est généré par l'API (moteur curriculum) sur les
 * SEMAINES COCHÉES avec leurs matières (2 slots de chapitre par jour — séances
 * de 2h, ~1h/chapitre) et rendu par le calendrier partagé
 * <SummerProgramCalendar /> (le calendrier affiche ce que le backend renvoie).
 * Généré à partir de classe / sessions × matières / objectif / niveau. Sur une
 * formule à la semaine, le parent peut le recomposer en une phrase
 * (« Adapter le programme », <StageProgramAdjustSheet />) : le
 * programme se recompose sous ses yeux, et ce qui a changé s'allume.
 *
 * L'écran ne s'affiche qu'une fois la composition complète (diagnostic +
 * ≥ 1 session cochée, chacune avec ≥ 1 matière) ; payload partiel → on route
 * vers la première étape amont capable de compléter (jamais de spinner infini).
 */

/** Les étapes d'une recomposition demandée par le parent. */
const RECOMPOSE_LINES = [
    "On relit votre demande",
    "On choisit les chapitres",
    "On répartit sur la semaine",
]

const chapterIdsOf = (program) =>
    new Set(
        (program?.schedule || [])
            .flatMap((week) => week.sessions || [])
            .map((session) => session.chapter?.id)
            .filter(Boolean)
    )

/** Le programme montre-t-il des chapitres de la classe de l'élève ? */
const showsOwnClass = (program) =>
    (program?.schedule || []).some((week) =>
        (week.sessions || []).some(
            (session) => session.chapter && !session.chapter.is_previous_class
        )
    )

const StagePlanScreen = ({ navigation }) => {
    const { state, update } = useStageFunnel()
    const track = useStageTrack()
    // Refresh-safe / URL-partageable (voir useStageFunnelGuard).
    const { nextParams } = useStageFunnelGuard()

    const [loading, setLoading] = useState(true)
    const [program, setProgram] = useState(null)
    const [failed, setFailed] = useState(false)
    // Retouche du programme (formules au créneau) : la séance en cours
    // d'enregistrement, et la modale de choix de chapitre.
    const [savingSeanceKey, setSavingSeanceKey] = useState(null)
    // La composition mise en scène : trois étapes qui se cochent, la dernière
    // seulement quand le programme est là. Reprend à chaque nouvelle composition.
    const [composing, setComposing] = useState(true)
    const [picker, setPicker] = useState(null)
    const [sheetOpen, setSheetOpen] = useState(false)
    // La recomposition se joue sur le programme : les séances s'estompent, les
    // étapes se cochent (la dernière quand la réponse est là, `recomposed`),
    // puis le nouveau programme revient séance par séance, ses chapitres
    // nouveaux allumés.
    const [recomposing, setRecomposing] = useState(false)
    const [recomposed, setRecomposed] = useState(null)
    const [changedChapterIds, setChangedChapterIds] = useState(null)
    const [programRevision, setProgramRevision] = useState(0)
    const scrollRef = useRef(null)

    // Id d'état valide requis (sur contexte froid, la garde route vers
    // stage/resume — on attend l'hydratation sans tirer l'appel).
    const funnelStateId = Number(state.funnelStateId)
    const hasValidStateId = Number.isInteger(funnelStateId) && funnelStateId > 0
    const hasDiagnostic =
        !!state.objective && !!state.studentClass && !!state.studentClassRentree
    // Composition v2 : ≥ 1 item coché ET chaque SESSION avec ≥ 1 matière.
    //
    // Le contrôle porte sur les clés de `sessionSubjects`, pas sur celles de
    // `sessions` : les deux coïncident en unité semaine, mais en unité créneau
    // les matières se rangent par WEEK-END quand les items sont des créneaux —
    // comparer les deux jeux de clés bloquerait l'écran sur une composition
    // pourtant complète.
    const sessions = state.sessions || []
    const sessionSubjects = state.sessionSubjects || {}
    // Les volumes horaires par matière voyagent avec la composition : c'est
    // eux qui répartissent les chapitres (2 h de Maths + 4 h de Français ne
    // donnent pas 3 h / 3 h).
    const sessionSubjectHours = state.sessionSubjectHours || {}
    // L'objectif visé par matière, aplati sur toutes les sessions : le
    // programme se lit par matière, et une matière porte le même objectif
    // partout où le parent l'a choisie.
    const subjectGoals = useMemo(
        () =>
            Object.values(state.sessionSubjectGoals || {}).reduce(
                (acc, perSubject) => ({ ...acc, ...(perSubject || {}) }),
                {}
            ),
        [state.sessionSubjectGoals]
    )
    // Trois étapes, sans promettre plus que ce qu'on fait : le niveau visé ne
    // change pas la liste des chapitres, et le parent la retouchera souvent.
    const composingLines = useMemo(() => {
        const count = sessions.length
        return [
            `On lit le programme de ${state.studentClass || "la classe"}`,
            "On choisit les chapitres",
            count > 0
                ? `On répartit sur ${count === 1 ? "votre créneau" : `vos ${count} créneaux`}`
                : "On répartit sur vos créneaux",
        ]
    }, [sessions.length, state.studentClass])
    const hasComposition =
        sessions.length > 0 &&
        Object.keys(sessionSubjects).length > 0 &&
        Object.values(sessionSubjects).every((ids) => (ids?.length || 0) > 0)
    const hasProgramPayload = hasValidStateId && hasDiagnostic && hasComposition

    // Première étape amont capable de compléter un payload partiel — pour ne
    // JAMAIS rester bloqué sur le reveal quand un champ manque.
    const incompleteStepRoute =
        !state.studentClass || !state.studentClassRentree
            ? "stage/start"
            : !state.objective
              ? "stage/diagnostic"
              : !hasComposition
                ? "stage/configurator"
                : null

    useEffect(() => {
        track("stage_funnel_plan_viewed")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Signature des entrées qui pilotent le programme — re-génère le reveal
    // quand le parent revient au configurateur, modifie la composition, puis
    // re-clique « Voir le programme » (l'écran reste monté en back-nav).
    // Composition v2 : le programme se compose sur les semaines cochées, avec
    // les matières de CHACUNE (2 slots de chapitre par jour côté backend). Un
    // seul objet pour la première composition ET les relectures après retouche.
    const programParams = useMemo(
        () => ({
            objective: state.objective,
            niveauActuel: state.niveauActuel,
            studentClass: state.studentClass,
            studentClassRentree: state.studentClassRentree,
            sessions,
            sessionSubjects,
            sessionSubjectHours,
            slotKey: state.slotKey,
        }),
        [
            state.objective,
            state.niveauActuel,
            state.studentClass,
            state.studentClassRentree,
            sessions,
            sessionSubjects,
            sessionSubjectHours,
            state.slotKey,
        ]
    )
    const programSignature = useMemo(
        () => JSON.stringify(programParams),
        [programParams]
    )
    const lastFetchedSignatureRef = useRef(null)

    useFocusEffect(
        useCallback(() => {
            if (!hasValidStateId) return
            if (!hasProgramPayload) {
                if (incompleteStepRoute) {
                    navigation.navigate(incompleteStepRoute, nextParams)
                } else {
                    setLoading(false)
                }
                return
            }
            if (
                lastFetchedSignatureRef.current === programSignature &&
                !failed
            ) {
                return
            }
            lastFetchedSignatureRef.current = programSignature
            let cancelled = false
            setLoading(true)
            setComposing(true)
            setFailed(false)
            stageFunnelService
                .getStageProgram({ stateId: funnelStateId }, programParams)
                .then((res) => {
                    if (cancelled) return
                    const payload = res?.data?.data ?? res?.data ?? null
                    setProgram(payload)
                    setChangedChapterIds(null)
                })
                .catch(() => {
                    if (cancelled) return
                    setFailed(true)
                    // Permet un retry au prochain focus.
                    lastFetchedSignatureRef.current = null
                })
                .finally(() => {
                    if (!cancelled) setLoading(false)
                })
            return () => {
                cancelled = true
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            hasValidStateId,
            hasProgramPayload,
            incompleteStepRoute,
            programSignature,
        ])
    )

    const firstname = state.studentFirstname || "votre enfant"

    // L'issue de secours : la famille garde le programme tel quel et décidera
    // des chapitres plus tard avec l'élève. Le choix voyage jusqu'à la demande
    // de tutorat, où le tuteur et l'ops le lisent.
    const decideLater = !!state.programDecidedLater
    const toggleDecideLater = () => {
        const next = !decideLater
        update({ programDecidedLater: next })
        if (hasValidStateId) {
            stageFunnelService
                .updateStageAnswers(
                    { stateId: funnelStateId },
                    { programDecidedLater: next }
                )
                .catch(() => {})
        }
    }

    /**
     * Recharge le programme depuis le serveur après une retouche.
     *
     * On refetch plutôt que de rafistoler l'état local : le serveur vient de
     * réécrire la séance, et les métadonnées dérivées (le « 1/2 · 2/2 » d'un
     * chapitre étalé) se recalculent chez lui. Une surgery locale les
     * laisserait périmées.
     */
    const refetchProgram = () =>
        stageFunnelService
            .getStageProgram({ stateId: funnelStateId }, programParams)
            .then((res) => {
                const payload = res?.data?.data ?? res?.data ?? null
                if (!payload) return
                setProgram(payload)
                setChangedChapterIds(null)
            })

    /**
     * Écrit la liste VOULUE de chapitres d'une séance. Le serveur revalide les
     * mêmes bornes que l'écran : la garde d'ici sert le confort, celle de
     * là-bas fait foi.
     */
    const writeSeances = (writes, failureTitle) => {
        if (!writes.length) return Promise.resolve()
        setSavingSeanceKey(writes[0].seance.key)
        // Chaque écriture ne touche que SA séance (programme, jour) dans sa
        // propre transaction côté serveur : elles partent ensemble.
        return Promise.all(
            writes.map((write) =>
                stageFunnelService.updateStageProgramSeance(
                    { stateId: funnelStateId },
                    {
                        studyProgramId: write.seance.studyProgramId,
                        day: write.seance.day,
                        chapterIds: write.chapterIds,
                    }
                )
            )
        )
            .then(refetchProgram)
            .catch(() => {
                // Le programme à l'écran reste celui du serveur : un échec ne
                // doit pas laisser croire que la retouche a pris.
                DialogM.show({
                    text1: failureTitle,
                    text2: "Vérifiez votre connexion et réessayez.",
                    exitButton: true,
                    onPressBack: () => {},
                })
                return refetchProgram()
            })
            .finally(() => setSavingSeanceKey(null))
    }

    const saveSeance = (seance, chapterIds) =>
        writeSeances(
            [{ seance, chapterIds }],
            "La modification n'a pas pu être enregistrée"
        )

    /**
     * Réordonne les chapitres d'UNE matière.
     *
     * Le parent déplace une ligne dans la liste qu'il lit ; les créneaux, eux,
     * ne bougent pas : ils reprennent les chapitres de la matière dans le
     * nouvel ordre, chacun gardant le NOMBRE qu'il avait. Un créneau partagé
     * avec une autre matière conserve les chapitres de celle-ci à leur place —
     * l'API réécrit la séance entière, pas une matière.
     *
     * Une seule relecture serveur à la fin, même si deux séances ont changé :
     * en rafraîchir une par écriture ferait clignoter le programme.
     */
    const reorderSubject = (block, from, to) => {
        const rows = block.rows
        if (from === to || !rows[from]) return
        const moved = rows.slice()
        moved.splice(to, 0, moved.splice(from, 1)[0])

        // Les créneaux de la matière, dans l'ordre où ils portaient ses
        // chapitres — c'est ce gabarit que la nouvelle suite vient remplir.
        const slots = []
        const seenSlot = new Set()
        rows.forEach(({ seance }) => {
            if (seenSlot.has(seance.key)) return
            seenSlot.add(seance.key)
            slots.push({ seance, count: 0 })
        })
        rows.forEach(({ seance }) => {
            slots.find((s) => s.seance.key === seance.key).count += 1
        })

        const writes = []
        let cursor = 0
        slots.forEach(({ seance, count }) => {
            const wanted = moved
                .slice(cursor, cursor + count)
                .map((r) => r.chapter.id)
            cursor += count
            const current = seance.chapters
                .filter((c) => c.subjectId === block.id)
                .map((c) => c.id)
            if (current.join() === wanted.join()) return
            // Les chapitres des AUTRES matières gardent leur position ; seules
            // les places de cette matière-ci sont réécrites.
            let i = 0
            const next = seance.chapters.map((c) =>
                c.subjectId === block.id ? wanted[i++] : c.id
            )
            writes.push({ seance, chapterIds: next })
        })
        writeSeances(writes, "Le nouvel ordre n'a pas pu être enregistré")
    }

    /**
     * Recompose le programme avec la phrase du parent — ou, phrase vide, revient
     * au programme proposé. Le serveur garde la phrase sur le parcours : les
     * relectures suivantes (qui ne l'envoient pas) retrouvent le même programme.
     *
     * La réponse est gardée de côté jusqu'à la dernière étape cochée
     * ({@link revealRecomposed}) : le parent voit le travail se faire, puis le
     * résultat arriver — jamais un programme qui change pendant qu'il lit.
     */
    const adjustProgram = (instructions) => {
        setSheetOpen(false)
        scrollRef.current?.scrollTo?.({ y: 0, animated: true })
        setRecomposed(null)
        setRecomposing(true)
        stageFunnelService
            .getStageProgram(
                { stateId: funnelStateId },
                { ...programParams, programInstructions: instructions }
            )
            .then((res) => {
                const payload = res?.data?.data ?? res?.data ?? null
                if (!payload) throw new Error("empty program")
                setRecomposed({
                    payload,
                    instructions,
                    signature: programSignature,
                })
            })
            .catch(() => {
                setRecomposing(false)
                DialogM.show({
                    text1: instructions
                        ? "Le programme n'a pas pu être recomposé"
                        : "Le programme proposé n'a pas pu être rétabli",
                    text2: "Vérifiez votre connexion et réessayez.",
                    exitButton: true,
                    onPressBack: () => {},
                })
            })
    }

    const revealRecomposed = () => {
        if (!recomposed) return
        // La composition a changé pendant la recomposition (retour au
        // configurateur) : la réponse décrit des semaines qui ne sont plus les
        // bonnes, et sa requête a réécrit le parcours avec elles. On la jette et
        // on recompose sur la composition d'aujourd'hui.
        if (recomposed.signature !== programSignature) {
            setRecomposing(false)
            refetchProgram()
            return
        }
        // Ce qui s'allume : les chapitres qui n'étaient pas à l'écran juste
        // avant. Un retour au programme proposé n'allume rien.
        const shown = chapterIdsOf(program)
        setChangedChapterIds(
            recomposed.instructions
                ? new Set(
                      [...chapterIdsOf(recomposed.payload)].filter(
                          (id) => !shown.has(id)
                      )
                  )
                : null
        )
        setProgram(recomposed.payload)
        setProgramRevision((revision) => revision + 1)
        setRecomposing(false)
        track(
            recomposed.instructions
                ? "stage_funnel_program_modified"
                : "stage_funnel_program_reset"
        )
    }

    const removeChapter = (seance, chapter) =>
        saveSeance(
            seance,
            seance.chapters.map((c) => c.id).filter((id) => id !== chapter.id)
        )

    const applyPickedChapter = (chapterId) => {
        if (!picker) return
        const { seance } = picker
        setPicker(null)
        saveSeance(seance, [...seance.chapters.map((c) => c.id), chapterId])
    }
    // La matière du sélecteur : celle du bloc où le parent a demandé un
    // chapitre — une séance porte une seule matière, l'ajout aussi.
    const pickerSubject = picker
        ? { id: Number(picker.block.id), name: picker.block.name }
        : null

    // Nom du stage : catalogue en priorité (une reprise directe sur cet écran
    // n'a pas encore de templateName en contexte — le hook le synchronise).
    const { config } = useStageFunnelConfig(state.funnelStateId)
    const templateName = getTemplateName(config) || state.templateName
    // Formule au créneau → programme retouchable. Les bornes viennent du
    // backend : la règle vit dans StageProgramLayout, l'écran la lit.
    // Le temps que la config arrive, l'unité se lit sur la forme des clés
    // cochées (`2026-09-12T10:00` = créneau) : sans ça, l'écran ouvrait sur
    // le spinner générique avant de basculer sur les étapes qui se cochent.
    const isSeanceUnit = config
        ? sellsBySeance(config)
        : sessions.some((key) => /T\d{2}:\d{2}$/.test(String(key)))
    const bounds = getChapterBounds(config)
    // La phrase que le serveur a appliquée revient avec chaque programme,
    // reprise comprise.
    const appliedInstructions = program?.program_instructions ?? null
    // Continuer attend un programme chargé, et aucune écriture en cours : partir
    // au checkout au milieu d'une écriture laisserait un programme que le
    // parent n'a pas vu.
    const continueBlocked =
        loading ||
        recomposing ||
        savingSeanceKey !== null ||
        (isSeanceUnit && composing)

    return (
        <FunnelStepLayout
            scene={FUNNEL_SCENES.programme}
            scrollRef={scrollRef}
            contentContainerStyle={styles.content}
            header={
                <FunnelStepHeader
                    step={4}
                    total={6}
                    // Pas de retour au milieu d'une recomposition : elle
                    // réécrit le parcours avec la composition affichée.
                    onBack={() => {
                        if (!recomposing) navigation.goBack()
                    }}
                />
            }
            footer={
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Continuer"
                        onPress={() =>
                            navigation.navigate("stage/tutors", nextParams)
                        }
                        disabled={continueBlocked}
                        color={
                            continueBlocked
                                ? theme.colors.lightMore
                                : theme.colors.primary
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            }
            overlay={
                <>
                    <StageChapterPickerModal
                        isVisible={!!picker}
                        onClose={() => setPicker(null)}
                        studentClass={state.studentClass}
                        subjectId={pickerSubject?.id}
                        subjectName={pickerSubject?.name}
                        slotLabel={
                            picker
                                ? `${capitalize(
                                      moment(picker.seance.date)
                                          .locale("fr")
                                          .format("ddd D MMM")
                                  )} · ${String(picker.seance.time).replace(":", "h")}`
                                : null
                        }
                        currentChapterId={null}
                        takenChapterIds={
                            picker
                                ? picker.seance.chapters.map((c) => c.id)
                                : []
                        }
                        onValidate={applyPickedChapter}
                    />
                    <StageProgramAdjustSheet
                        isVisible={sheetOpen}
                        onClose={() => setSheetOpen(false)}
                        appliedInstructions={appliedInstructions}
                        firstname={firstname}
                        studentClass={state.studentClass}
                        previousClass={program?.previous_class}
                        onRecompose={adjustProgram}
                    />
                </>
            }
        >
            <SummerEyebrow label={`📚 ${templateName || "Stage"}`} />
            <Text style={styles.title}>Le programme {elide(firstname)}</Text>

            {/* Formule au créneau : le programme est RETOUCHABLE, donc
                rendu par son propre composant. SummerProgramCalendar
                sert l'été, le brevet et les stages à la semaine en
                lecture seule — y greffer des actions ferait porter à
                trois produits le risque d'un quatrième. */}
            {isSeanceUnit && !failed && (loading || composing) ? (
                <View style={styles.composing}>
                    <FunnelStepTicks
                        lines={composingLines}
                        done={!loading}
                        onFinished={() => setComposing(false)}
                    />
                </View>
            ) : isSeanceUnit && !failed ? (
                <>
                    <Text style={styles.editHint}>
                        On a composé le programme à partir de vos matières. Vous
                        pouvez l'ajuster chapitre par chapitre.
                    </Text>
                    <StageEditableProgram
                        program={program}
                        bounds={bounds}
                        subjectGoals={subjectGoals}
                        savingSeanceKey={savingSeanceKey}
                        onAdd={(block, seance) => setPicker({ block, seance })}
                        onRemove={removeChapter}
                        onReorder={reorderSubject}
                    />
                    <TouchableOpacity
                        style={[
                            styles.laterRow,
                            decideLater && styles.laterRowOn,
                        ]}
                        activeOpacity={0.8}
                        onPress={toggleDecideLater}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: decideLater }}
                    >
                        <FontAwesome5
                            name={decideLater ? "check-square" : "square"}
                            solid={decideLater}
                            size={18}
                            color={
                                decideLater
                                    ? theme.colors.primary
                                    : theme.colors.lightMore
                            }
                        />
                        <View style={styles.laterTexts}>
                            <Text style={styles.laterLabel}>
                                Je préfère décider des chapitres plus tard avec{" "}
                                {firstname}
                            </Text>
                            <Text style={styles.laterHint}>
                                On garde ce programme pour l'instant. Il pourra
                                être ajusté avant le stage.
                            </Text>
                        </View>
                    </TouchableOpacity>
                </>
            ) : (
                <>
                    {/* Un stage pendant l'année : le programme est une
                        estimation, et l'écran dit sur quoi elle repose —
                        ce qui est vraiment affiché, pas ce qu'on espérait. */}
                    {!!program?.previous_class &&
                        !loading &&
                        !appliedInstructions && (
                            <Text style={styles.basisHint}>
                                {showsOwnClass(program)
                                    ? `Construit sur les bases de ${program.previous_class} et les chapitres de ${state.studentClass} vus d'habitude à cette période de l'année.`
                                    : `Construit sur les bases de ${program.previous_class} : à cette période de l'année, on consolide ce que ${firstname} a déjà vu.`}
                            </Text>
                        )}
                    {!!program && !loading && !failed && (
                        <StageProgramAdjustEntry
                            appliedInstructions={appliedInstructions}
                            busy={recomposing}
                            onOpen={() => setSheetOpen(true)}
                            onReset={() => adjustProgram("")}
                        />
                    )}
                    <View>
                        <View style={recomposing && styles.programDimmed}>
                            <SummerProgramCalendar
                                key={programRevision}
                                program={program}
                                firstname={firstname}
                                loading={loading}
                                failed={failed}
                                // Chips matières : le payload stage porte le
                                // TOTAL d'heures planifié sur toutes les
                                // sessions cochées (« Maths 15h »).
                                subjectHoursMode="total"
                                // ≥ 4ème : un chapitre couvre les 2 créneaux
                                // du jour — affiché « · 1/2 » / « · 2/2 ».
                                showParts
                                // Le rythme est déjà porté par les sessions
                                // choisies — le sous-titre n'apporte rien.
                                hideSubtitle
                                enterAnimation={programRevision > 0}
                                highlightChapterIds={changedChapterIds}
                            />
                        </View>
                        {recomposing && (
                            <View style={styles.recomposeOverlay}>
                                <View style={styles.recomposeCard}>
                                    <FunnelStepTicks
                                        lines={RECOMPOSE_LINES}
                                        done={!!recomposed}
                                        onFinished={revealRecomposed}
                                    />
                                </View>
                            </View>
                        )}
                    </View>
                </>
            )}
            <SummerHelpBanner
                track={track}
                step={4}
                trackingEvent="stage_funnel_help_clicked"
            />
        </FunnelStepLayout>
    )
}

const styles = StyleSheet.create({
    content: { paddingBottom: 24 },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 12,
    },
    composing: { paddingVertical: 18 },
    laterRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 12,
        marginTop: 16,
        padding: 14,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    laterRowOn: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    laterTexts: { flex: 1, gap: 3 },
    laterLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.primaryDark || theme.colors.primary,
        lineHeight: 18,
    },
    laterHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        lineHeight: 16,
    },
    programDimmed: { opacity: 0.25 },
    recomposeOverlay: {
        position: "absolute",
        top: 24,
        left: 0,
        right: 0,
        alignItems: "center",
    },
    recomposeCard: {
        minWidth: 260,
        paddingVertical: 16,
        paddingHorizontal: 18,
        borderRadius: 16,
        backgroundColor: theme.colors.white,
        shadowColor: theme.colors.primaryDark || theme.colors.primary,
        shadowOpacity: 0.18,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 10 },
        elevation: 6,
    },
    basisHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12.5,
        color: theme.colors.grey,
        lineHeight: 18,
        marginBottom: 12,
    },
    editHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        lineHeight: 17,
        marginBottom: 14,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

export default StagePlanScreen
